import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {WebSocketChannels, WebSocketEvents, WebSocketsClient} from '../../../websockets/WebSocketsClient';
import {applyPlanFor, destinationForErrors, signatureOf} from '../applyPlan';
import {
    PHASE_CAMPAIGNS, PHASE_DASHBOARD, PHASE_METRICS, REASON_NO_CONNECTION,
    clientKeyOf, clientsForClosing, clientsFromProgress, elapsedMinutesOf, hasClientsNotReady, isSettled,
    nextPollMs, summaryOf, trackStageSince,
} from '../applyProgress';
import {fetchApplyProgress, postAddBulk} from '../api';
import {buildAddBulkPayload, parseAddBulk422} from '../wizardPayload';
import {cachedStateFor, ownerKey, rememberState} from '../wizardMemory';
import {wizardReducer} from '../wizardReducer';
import {useOnboarding} from '../wizardContext';
import './applyProgress.scss';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';

/**
 * La espera mientras se crea la cartera.
 *
 * Cuenta, por cliente y por nombre, qué ya está listo y qué se está haciendo ahora con cada uno
 * («Trayendo las campañas · Cuenta X»). Antes mostraba sólo «3 de 4 listos», y con una cuenta que
 * tardaba no había forma de saber si avanzaba o se había colgado: el usuario se iba (card t3XCXPg6).
 *
 * El progreso sale del servidor (`apply-progress`), no de los avisos en vivo: así sobrevive a que el
 * usuario salga y vuelva y no depende de un evento que se pierde. El aviso en vivo sigue, como
 * acelerador: cuando llega, se vuelve a preguntar en el momento.
 *
 * Medido el 23/09 (spike del camino frío): con cuentas que Dashbo no conoce una línea tarda de menos
 * de un minuto a 16, y el tramo largo es traer las campañas. Por eso la pantalla lo dice, y ofrece
 * seguir mientras termina: retener al usuario hasta la última cuenta de una cartera grande no le
 * sirve a nadie.
 *
 * «Seguir mientras termina» está A LA VISTA desde el principio, deshabilitado, y se habilita con el
 * primer cliente listo (card 9WXeowAo, pedido de fede del 30/09). Antes aparecía a los 20 s: hasta
 * ahí la pantalla no decía que existía una salida, y a partir de ahí la ofrecía aunque no hubiera un
 * solo cliente con su tablero.
 */

// El respaldo por si el servidor nunca dice «terminé». Largo: una sola línea fría llega a 16 min.
const MAX_WAIT_MS = 20 * 60 * 1000;
// Sin ids (alta huérfana), cuántas respuestas vacías se aceptan antes de concluir que no se creó nada.
const EMPTY_ORPHAN_POLLS = 3;
// Cuánto se ve «todo listo» (barra llena, lista en verde) antes de pasar al cierre.
const ALL_READY_PAUSE_MS = 900;
// Cada cuánto se redibuja el tiempo de las filas. Cuenta en minutos: más seguido no cambia nada.
const ELAPSED_TICK_MS = 15000;
// Ata el texto de al lado a «Seguir mientras termina»: deshabilitado, es lo único que dice por qué.
const CONTINUE_HINT_ID = 'ob-apply-continue-hint';

/**
 * Las carteras que están posteándose ahora mismo.
 *
 * Vive a nivel de módulo y no en un `ref` porque en desarrollo React monta los efectos DOS veces
 * sobre la misma cartera, y las dos corridas leen `applyStatus === 'idle'` (el `dispatch` todavía no
 * se aplicó). Sin esto el alta se postea dos veces y, como ninguna de las dos ve las cuentas que la
 * otra acaba de cubrir, se crea todo duplicado. Un `ref` no sirve: se recrea en cada montaje, que es
 * justo el caso que hay que atajar.
 *
 * Es además lo que distingue un alta EN VUELO de una HUÉRFANA: si el estado dice `running` y la firma
 * no está acá, el POST ya volvió sobre una pantalla que no estaba.
 */
const inFlight = new Set();

/**
 * El resultado de un POST que volvió cuando su pantalla ya no estaba, por firma de cartera.
 *
 * Con el usuario afuera el `dispatch` no llega a ningún reducer. La memoria de la pestaña lo
 * recibe, pero la pantalla que se monta al volver ya arrancó con el estado viejo y no la relee: sin
 * esto, un 422 con errores por fila terminaba en un error genérico sin marcar las filas, y un alta
 * que no creó nada se leía como un fallo. Se adopta la acción tal cual, que es lo que habría pasado
 * con la pantalla montada.
 */
const settledWhileAway = new Map();

const PHASE_KEYS = {
    [PHASE_CAMPAIGNS]: 'onboarding.applying.phase_campaigns',
    [PHASE_METRICS]: 'onboarding.applying.phase_metrics',
    [PHASE_DASHBOARD]: 'onboarding.applying.phase_dashboard',
};

export function ApplyInterstitial() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {state, dispatch, server} = useOnboarding();
    const [syncedIds, setSyncedIds] = useState([]);
    const [progress, setProgress] = useState(null);
    const [orphan, setOrphan] = useState(false);
    // La espera terminó sin que todo quedara listo (algún fallido, o el tope con clientes pendientes):
    // la pantalla se queda a decirlo en vez de navegar a un «Listo» que no es cierto.
    const [endedWithIssues, setEndedWithIssues] = useState(false);
    // Todo listo: un instante con la barra llena y la lista en verde antes de navegar, para que el
    // final se vea (antes saltaba de «2 de 13» a otra pantalla en la misma respuesta).
    const [allReady, setAllReady] = useState(false);
    // El progreso por cliente no respondió: recién ahí vale el contador viejo por líneas. Mientras
    // no respondió todavía, mostrarlo hacía que el total saltara de líneas a clientes frente al usuario.
    const [progressFailed, setProgressFailed] = useState(false);
    // Cambia cuando el POST de un montaje anterior dejó de estar en vuelo: vuelve a correr el efecto
    // que decide qué hacer, que primero busca su resultado en `settledWhileAway`.
    const [awayChecks, setAwayChecks] = useState(0);
    // Desde cuándo cada cliente está en lo que dice su fila (`trackStageSince`), y el reloj que la
    // redibuja: sin esto, una fila que tarda minutos no se distingue de una colgada.
    const [stages, setStages] = useState(() => new Map());
    const [now, setNow] = useState(() => Date.now());
    const mounted = useRef(true);
    const pollNow = useRef(null);
    // Lo último que se supo del progreso, para el tope y para el cierre: los timers no pueden leer
    // el estado de React.
    const latestProgress = useRef(null);
    latestProgress.current = progress;

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const payload = useMemo(() => buildAddBulkPayload({
        decisions: state.decisions, candidates: server.candidates, budgets: server.budgets,
        budgetEdits: state.budgetEdits, conversionEvents: server.conversionEvents,
        conversionEdits: state.conversionEdits,
    }), [state.decisions, state.budgetEdits, state.conversionEdits, server.candidates, server.budgets,
        server.conversionEvents]);

    const applyStatus = state.apply.status;
    const payloadSignature = useMemo(() => signatureOf(payload.items), [payload.items]);
    const alreadyPosted = state.apply.payloadSignature;
    const createdIds = state.apply.createdIds;

    useEffect(() => {
        if (applyStatus === 'running' && settledWhileAway.has(alreadyPosted)) {
            const action = settledWhileAway.get(alreadyPosted);
            settledWhileAway.delete(alreadyPosted);
            dispatch(action);
            if (action.type === 'APPLY_FAILED') navigate(destinationForErrors(action.rowErrors), {replace: true});
            return undefined;
        }
        const plan = applyPlanFor({
            status: applyStatus, alreadyPosted, signature: payloadSignature,
            rowErrors: state.apply.rowErrors, hasItems: payload.items.length > 0,
            postInFlight: inFlight.has(alreadyPosted),
        });
        if (plan.action === 'track') {
            setOrphan(true);
            return;
        }
        if (plan.action === 'wait') {
            if (applyStatus !== 'running') return undefined;
            // El POST puede ser de un montaje ANTERIOR de esta pantalla (el usuario salió y volvió
            // en la misma pestaña): cuando vuelva, su resultado no llega a este reducer. Al dejar de
            // estar en vuelo se vuelve a decidir: se adopta su resultado o, si no está, se sigue
            // desde el servidor.
            const check = setInterval(() => {
                if (!inFlight.has(alreadyPosted)) setAwayChecks(count => count + 1);
            }, 2000);
            return () => clearInterval(check);
        }
        if (plan.action === 'navigate') {
            if (applyStatus !== 'idle' || payload.items.length) {
                navigate(plan.to, {replace: true});
                return undefined;
            }
            // Sin nada para crear y sin alta en memoria: es la vuelta después de un F5 (la memoria de
            // la pestaña se perdió). Si la agencia tiene un alta reciente que todavía avanza, se la
            // muestra en vez de rebotar a clientes, que dejaba al usuario sin forma de ver cómo iba.
            let cancelled = false;
            fetchApplyProgress(null).then(response => {
                if (cancelled) return;
                const recent = response?.status === 'OK' ? response.data : null;
                if (recent && (recent.lines || []).length && recent.poll?.pending) {
                    setOrphan(true);
                    return;
                }
                navigate(plan.to, {replace: true});
            });
            return () => { cancelled = true; };
        }

        // Se llega acá sólo con un pedido del usuario (`idle` o `requested`, ver `applyPlanFor`). La
        // firma ya no decide si se postea: acá sólo evita el doble POST del doble montaje de desarrollo.
        if (inFlight.has(payloadSignature)) return;
        inFlight.add(payloadSignature);

        dispatch({type: 'APPLY_STARTED', payloadSignature});
        postAddBulk(payload.items).finally(() => inFlight.delete(payloadSignature)).then(response => {
            if (response?.status === 'OK') {
                const data = response.data || {};
                settle({
                    type: 'APPLY_SUCCEEDED',
                    createdIds: data.created_business_unit_ids || [],
                    createdClients: data.created_clients || [],
                    skipped: data.skipped_already_covered || [],
                });
                return;
            }
            // Cualquier otra cosa (422 con errores por ítem, error de red) vuelve al paso donde el
            // usuario puede arreglarlo, con las filas marcadas. Nunca se le muestra el mensaje crudo
            // del backend.
            const parsed = parseAddBulk422(response, payload.indexToPk);
            settle({
                type: 'APPLY_FAILED',
                rowErrors: parsed.byPk,
                requestErrors: parsed.requestErrors,
                createdClients: parsed.createdClients,
                isPartial: parsed.isPartial,
                // Sin ningún error ubicable (una caída de red, por ejemplo) hay que decir algo
                // igual: rebotar en silencio al paso anterior se lee como que la pantalla se colgó.
                genericError: Object.keys(parsed.byPk).length ? null : 'generic',
            });
            if (mounted.current) navigate(destinationForErrors(parsed.byPk), {replace: true});
        });

        /**
         * El resultado del POST, también si la pantalla ya no está.
         *
         * Con el usuario afuera («Guardar y seguir después» en medio del pedido) el `dispatch` cae
         * sobre un reducer desmontado y se pierde. Se escribe entonces en la memoria de la pestaña,
         * que es de donde el recorrido arranca al volver: los ids y los clientes creados llegan igual.
         */
        function settle(action) {
            if (mounted.current) {
                dispatch(action);
                return;
            }
            settledWhileAway.set(payloadSignature, action);
            const owner = ownerKey();
            const cached = cachedStateFor(owner);
            if (cached) rememberState(wizardReducer(cached, action), owner);
        }
    }, [applyStatus, alreadyPosted, payloadSignature, payload, state.apply.rowErrors, dispatch, navigate, awayChecks]);

    const tracking = applyStatus === 'syncing' || orphan;
    // En una alta huérfana los ids se perdieron: el servidor devuelve las líneas del alta reciente.
    const trackedIds = orphan ? null : createdIds;

    // El cierre se lleva cuántos clientes estaban listos al salir: con «Seguir mientras termina» o
    // con el tope, la pantalla final decía «Listo: tu cartera está en Dashbo» con 3 de 5 listos
    // (card 9WXeowAo, D5). Sin progreso por cliente (backend viejo) no viaja nada y el cierre queda
    // como estaba.
    const finish = useMemo(() => () => {
        const {total, ready, failed} = summaryOf(clientsFromProgress(latestProgress.current?.lines));
        dispatch({type: 'SYNC_FINISHED', readiness: total ? {ready, total, failed} : null});
        navigate('/onboarding/listo', {replace: true});
    }, [dispatch, navigate]);

    // Un pedido que no creó ninguna línea es un ÉXITO, no un error: son cuentas que ya estaban
    // cubiertas. Sin esta salida la pantalla se quedaba esperando algo que nunca iba a llegar.
    useEffect(() => {
        if (applyStatus === 'syncing' && !orphan && !createdIds.length) finish();
    }, [applyStatus, orphan, createdIds, finish]);

    // El progreso por línea, preguntado cada pocos segundos mientras algo pueda cambiar.
    useEffect(() => {
        if (!tracking || (trackedIds && !trackedIds.length)) return undefined;
        let cancelled = false;
        let timer = null;
        let emptyOrphanPolls = 0;

        // El alta huérfana no dejó ninguna línea a la vista: falló mientras el usuario no estaba, no
        // había nada que crear, o el backend todavía no sabe contestar el progreso. Sin ids no hay
        // otra señal que esperar, así que se vuelve a los clientes, que se reconcilian contra el
        // servidor y muestran lo que de verdad hay.
        const orphanFoundNothing = () => {
            emptyOrphanPolls += 1;
            if (emptyOrphanPolls < EMPTY_ORPHAN_POLLS) return false;
            dispatch({type: 'APPLY_FAILED', rowErrors: {}, createdClients: [], isPartial: false,
                      genericError: 'generic'});
            navigate('/onboarding/clientes', {replace: true});
            return true;
        };

        // Sólo cuenta la respuesta del ÚLTIMO pedido. El aviso en vivo llama a `poll` con otro en
        // vuelo: sin esto, la respuesta más vieja llegaba después, pisaba el progreso con una fase
        // atrasada (reiniciando el reloj de la fila) y cada respuesta agendaba su propio siguiente
        // pedido, así que quedaban dos cadenas de polling vivas por cada evento (C-tunel-01).
        let generation = 0;

        const poll = () => {
            clearTimeout(timer);
            generation += 1;
            const mine = generation;
            fetchApplyProgress(trackedIds).then(response => {
                if (cancelled || mine !== generation) return;
                // Un error se calla y se vuelve a preguntar: la lista anterior sigue siendo la mejor
                // información que hay, y el aviso en vivo cubre el contador mientras tanto. En una
                // alta huérfana, en cambio, no hay contador de respaldo: cuenta como «nada a la vista».
                if (response?.status !== 'OK') {
                    setProgressFailed(true);
                    if (orphan && orphanFoundNothing()) return;
                    timer = setTimeout(poll, nextPollMs(null));
                    return;
                }
                const data = response.data || {};
                if (orphan && !(data.lines || []).length && orphanFoundNothing()) return;
                setProgressFailed(false);
                setProgress(data);
                setStages(previous => trackStageSince(previous, clientsFromProgress(data.lines), Date.now()));
                if (isSettled(data)) {
                    // Si algún cliente quedó con problemas, la pantalla se queda: navegar sola a
                    // «Listo: tu cartera está en Dashbo» presentaba un fallo como un éxito, y con un
                    // solo cliente el usuario no llegaba a verlo nunca.
                    if (summaryOf(clientsFromProgress(data.lines)).failed) {
                        setEndedWithIssues(true);
                        return;
                    }
                    setAllReady(true);
                    timer = setTimeout(finish, ALL_READY_PAUSE_MS);
                    return;
                }
                timer = setTimeout(poll, nextPollMs(data));
            });
        };
        pollNow.current = poll;
        poll();

        return () => {
            cancelled = true;
            clearTimeout(timer);
            pollNow.current = null;
        };
    }, [tracking, trackedIds, orphan, dispatch, navigate, finish]);

    // El aviso en vivo: adelanta la próxima pregunta y alimenta el contador si el servidor todavía
    // no sabe contestar el progreso (los dos repos no se despliegan a la vez).
    useEffect(() => {
        if (!tracking) return undefined;
        const unbind = WebSocketsClient.bind(
            WebSocketChannels.BU_DASHBOARD_SYNC, WebSocketEvents.BU_DASHBOARD_SYNCED, event => {
                const id = event?.businessUnitId;
                if (id === undefined || id === null) return;
                // Se guardan los ids y no un contador: el mismo tablero puede avisar dos veces (un
                // reintento del sync) y el progreso llegaría al final antes que los datos.
                setSyncedIds(previous => (previous.includes(String(id)) ? previous
                                                                       : previous.concat([String(id)])));
                if (pollNow.current) pollNow.current();
            });
        return () => {
            if (typeof unbind === 'function') unbind();
        };
    }, [tracking]);

    // El reloj de las filas, mientras alguna pueda seguir trabajando. Sigue con `endedWithIssues`:
    // ahí el poll continúa y una fila todavía en curso se quedaba con el tiempo congelado.
    useEffect(() => {
        if (!tracking || allReady) return undefined;
        const tick = setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS);
        return () => clearInterval(tick);
    }, [tracking, allReady]);

    useEffect(() => {
        if (!tracking || endedWithIssues || allReady) return undefined;
        // Al tope, si queda algún cliente sin terminar (un sync que nunca arrancó, típicamente), la
        // pantalla se queda y lo dice: ir directo a «Listo» era el mismo falso éxito de un fallo final.
        const giveUp = setTimeout(() => {
            if (hasClientsNotReady(clientsFromProgress(latestProgress.current?.lines))) {
                setEndedWithIssues(true);
                return;
            }
            finish();
        }, MAX_WAIT_MS);
        return () => clearTimeout(giveUp);
    }, [tracking, endedWithIssues, allReady, finish]);

    // Sin respuesta del progreso (backend anterior a este frente), el contador de antes: los avisos
    // en vivo contra los ids creados.
    const legacySynced = createdIds.filter(id => syncedIds.includes(String(id))).length;
    useEffect(() => {
        if (!progress && createdIds.length > 0 && legacySynced >= createdIds.length) finish();
    }, [progress, legacySynced, createdIds, finish]);

    const clients = useMemo(() => clientsFromProgress(progress?.lines), [progress]);
    const summary = summaryOf(clients);

    // Terminó (bien o mal) y el efecto de arriba está navegando: la barra de progreso acá ya no
    // describe nada. Mostrarla un instante más es lo que se leía como «se colgó».
    if (!['idle', 'requested', 'running', 'syncing'].includes(applyStatus)) return null;

    const useLegacyCounter = progressFailed && !summary.total && createdIds.length > 0;
    // Se puede seguir cuando HAY algo que ver del otro lado: el primer cliente con su tablero. Con el
    // backend viejo (sin progreso por cliente) lo dice el aviso en vivo del primer tablero.
    const firstClientReady = summary.ready > 0 || (useLegacyCounter && legacySynced > 0);
    const canContinue = tracking && firstClientReady;
    // La barra dice el avance real en cuanto hay lista: un 8 % debajo de «0 de 2 listos» contradecía el
    // número. El piso sólo vale mientras se prepara, cuando todavía no hay nada que contar.
    let percent = 8;
    if (allReady) percent = 100;
    else if (summary.total) percent = Math.round((summary.ready / summary.total) * 100);
    else if (useLegacyCounter) percent = Math.max(Math.round((legacySynced / createdIds.length) * 100), 8);

    const shownClients = endedWithIssues ? clientsForClosing(clients) : clients;
    let titleKey = 'onboarding.applying.title';
    if (endedWithIssues) titleKey = 'onboarding.applying.title_with_issues';
    else if (allReady) titleKey = 'onboarding.applying.title_ready';

    return (
        <div className={`ob-interstitial ob-apply${clients.length ? ' -with-list' : ''}`}>
            {!endedWithIssues && !allReady && <span className="ob-spinner-big" aria-hidden="true"/>}
            <h1 {...CLARITY_UNMASK}>{t(titleKey)}</h1>
            <p className="ob-interstitial__detail" aria-live="polite">
                {headline({t, summary, createdIds, legacySynced, useLegacyCounter})}
            </p>
            <div className="ob-progress">
                <span className="ob-progress__fill" style={{width: `${percent}%`}}/>
            </div>

            {shownClients.length > 0 && (
                <ul className="ob-apply-clients" aria-label={t('onboarding.applying.clients_label')}>
                    {shownClients.map(client => (
                        <li key={client.id ?? client.name} className={`ob-apply-client -${client.status}`}>
                            <span className="ob-apply-client__mark" aria-hidden="true"/>
                            <span className="ob-apply-client__name">{client.name}</span>
                            <span className="ob-apply-client__step">
                                {stepText(t, client,
                                          elapsedMinutesOf(client, stages.get(clientKeyOf(client))?.since, now))}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {endedWithIssues && (
                <>
                    <p className="ob-apply-note">
                        {summary.failed
                            ? t('onboarding.applying.failed_note', {count: summary.failed})
                            : t('onboarding.applying.still_pending_note')}
                    </p>
                    <div className="ob-apply-continue">
                        <button type="button" className="ob-btn -primary" onClick={finish} {...CLARITY_UNMASK}>
                            {t('onboarding.applying.continue_anyway')}
                        </button>
                    </div>
                </>
            )}
            {tracking && !endedWithIssues && !allReady && (
                <p className="ob-apply-note">{t('onboarding.applying.slow_note')}</p>
            )}
            {/* Desde el principio, también mientras el alta todavía se postea: la salida existe y
                se ve dónde está. El texto de al lado dice por qué todavía no se puede y queda atado
                al botón (`aria-describedby`), porque un `<button disabled>` sale del orden de Tab. */}
            {!endedWithIssues && !allReady && (
                <div className="ob-apply-continue">
                    <button type="button" className="ob-btn -primary" onClick={finish} disabled={!canContinue} {...CLARITY_UNMASK}
                            aria-describedby={CONTINUE_HINT_ID}>
                        {t('onboarding.applying.continue')}
                    </button>
                    <span id={CONTINUE_HINT_ID}>
                        {canContinue ? t('onboarding.applying.continue_hint')
                                     : t('onboarding.apply.continue_waiting')}
                    </span>
                </div>
            )}
            <p className="ob-interstitial__reward">{t('onboarding.applying.reward')}</p>
        </div>
    );
}

/**
 * «<b>2 de 13</b> clientes listos · 1 con problemas · 1 reintentando».
 *
 * El número va en negrita, como en el mockup. Toda fila ámbar tiene su número acá: antes el que se
 * reintentaba no sumaba y «1 con problemas» convivía con dos filas ámbar.
 */
function headline({t, summary, createdIds, legacySynced, useLegacyCounter}) {
    if (summary.total) {
        const extras = [];
        if (summary.failed) extras.push(t('onboarding.applying.clients_failed', {count: summary.failed}));
        if (summary.retrying) extras.push(t('onboarding.applying.clients_retrying', {count: summary.retrying}));
        return (
            <>
                <Trans i18nKey="onboarding.applying.clients_progress" count={summary.total}
                       values={{ready: summary.ready}} components={{b: <b/>}}/>
                {extras.map(extra => ` · ${extra}`).join('')}
            </>
        );
    }
    if (useLegacyCounter) {
        return t('onboarding.applying.progress', {done: legacySynced, total: createdIds.length});
    }
    return t('onboarding.applying.starting');
}

/**
 * «Trayendo las campañas · Cuenta X · hace 3 min». El tiempo va sólo mientras la fila trabaja y
 * desde el primer minuto (`elapsedMinutesOf`): es lo que muestra que avanza.
 */
function stepText(t, client, elapsedMinutes = null) {
    if (client.status === 'ready') return t('onboarding.applying.phase_ready');
    if (client.status === 'queued') return t('onboarding.applying.phase_queued');
    if (client.status === 'retrying') return t('onboarding.applying.phase_retrying');
    if (client.status === 'failed') {
        return client.reason === REASON_NO_CONNECTION ? t('onboarding.applying.phase_failed_no_connection')
                                                      : t('onboarding.applying.phase_failed');
    }
    const parts = [t(PHASE_KEYS[client.phase] || 'onboarding.applying.phase_campaigns')];
    if (client.accountName) parts.push(client.accountName);
    if (elapsedMinutes !== null) parts.push(t('onboarding.apply.elapsed', {minutes: elapsedMinutes}));
    return parts.join(' · ');
}
